import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { CompanyData } from '@/features/companies/types/companies.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { useUpdateGeneralPolitics } from '@/features/privateCompanies/hooks/politics/useUpdateGeneralPolitics'
import { updateGeneralPoliticsSchema, UpdateGeneralPoliticsSchema } from '@/features/privateCompanies/schemas/politics/updateGeneralPolitics.schema'
import SelectController from '@/common/components/SelectController'
import { useEstablishmentFlags } from '@/common/hooks/queries/useEstablishmentFlags'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { Flag } from '@/common/types/establishment/flag.type'
import { blockedFlagsModeOptions } from '@/features/privateCompanies/constants/politics/viewingPolitics/blockedFlagsMode.constant'

interface UpdateSupplyPoliticsModalProps {
  company?: CompanyData
}

export const UpdateViewingPoliticsModal = ({ company }: UpdateSupplyPoliticsModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchFlag, setSearchFlag] = useDebounce('');

  const { data: establishmentFlagOptions } = useEstablishmentFlags({
    filters: {
      search: searchFlag,
    },
    mapper: Mapper.mapToOptions<Flag>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const formMethods = useForm<UpdateGeneralPoliticsSchema>({
    resolver: zodResolver(updateGeneralPoliticsSchema),
    defaultValues: {
      company: {
        allowed_establishment_flags: Mapper.mapToOptions<CompanyData['allowed_establishment_flags'][number], true, true>({
          labelFieldName: ['establishment_flag', 'display_name'],
          valueFieldName: ['establishment_flag', 'id'],
          nestedLabelField: true,
          nestedValueField: true,
        })(company?.allowed_establishment_flags || []),
        establishment_blocked_flags_mode: blockedFlagsModeOptions.find(({ value }) => value === company?.establishment_blocked_flags_mode),
      },
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  const {
    mutate: updatePolitics,
    isPending: updatePoliticsIsPending,
  } = useUpdateGeneralPolitics({
    companyId: company?.id,
    company,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', company?.id] })
      setIsOpen(false)
    },
    onError: (error) => {
      setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao atualizar as políticas de visualização'])
    },
  })

  const onSubmit: SubmitHandler<UpdateGeneralPoliticsSchema> = (data) => {
    updatePolitics(data);
  }

  useEffect(() => {
    if (isOpen) {
      reset({
        company: {
          allowed_establishment_flags: Mapper.mapToOptions<CompanyData['allowed_establishment_flags'][number], true, true>({
            labelFieldName: ['establishment_flag', 'display_name'],
            valueFieldName: ['establishment_flag', 'id'],
            nestedLabelField: true,
            nestedValueField: true,
          })(company?.allowed_establishment_flags || []),
          establishment_blocked_flags_mode: blockedFlagsModeOptions.find(({ value }) => value === company?.establishment_blocked_flags_mode),
        }
      });
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        bodyClassName='overflow-visible'
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            operationKlassToCheckPermission="Admin::Client::Companies::UpdatePoliticsGeneralInformation"
            name="open_update_viewing_politics_modal"
            className="h-[2.125rem] text-sm text-white-800"
          >
            Editar
          </Button>
        )}
        title="Editando Políticas de Visualização"
        dataTestid="submit_update_viewing_politics_button"
        useCancellableFooter
        confirmButtonClassName="bg-blue-700 rounded-br-lg"
        cancelButtonClassName="bg-white-100 rounded-bl-lg"
        disabled={updatePoliticsIsPending}
        confirmButtonIsLoading={updatePoliticsIsPending}
        onConfirm={handleSubmit(onSubmit)}
        body={
          <div className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="company.allowed_establishment_flags"
              isMulti
              isClearable
              options={establishmentFlagOptions}
              onInputChange={setSearchFlag}
              label='Bandeiras permitidas'
              errorMessage={errors.company?.allowed_establishment_flags?.message || undefined}
            />

            <SelectController
              control={control}
              name="company.establishment_blocked_flags_mode"
              options={blockedFlagsModeOptions}
              label='Demais bandeiras restringir'
              errorMessage={errors.company?.establishment_blocked_flags_mode?.message || undefined}
            />
          </div>
        }
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  )
}
